/**
 * Vista de Inicio de Sesión — LoginView
 * Diseñado con la misma línea gráfica y paleta de Registro / Onboarding (#c25ee8).
 * En móvil: sólo el encabezado es a color, la tarjeta blanca cubre todo el resto.
 * Incluye botones de soporte de WhatsApp y Elie (tanto en móvil como en escritorio).
 */

'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
  ArrowLeft,
  Loader2,
  AlertCircle,
  UserPlus,
} from 'lucide-react';
import { ROUTES } from '@/config/routes';
import { useAuthFlow } from '../hooks/use-auth-flow';
import { MfaChallengeStep } from '../components/MfaChallengeStep';
import {
  loginCredencialesSchema,
  LoginCredencialesSchemaType,
} from '../schemas/auth.schema';
import { LanguageSelectorPill } from '@/modulos/onboarding_clientes/components/LanguageSelectorPill';
import {
  SocialIndividualModal,
  IdiomaModalSelector,
  type RedSocialId,
  type IdiomaItem,
} from '@/shared/components/modals';

const IDIOMAS: IdiomaItem[] = [
  {
    code: 'es',
    label: 'Español',
    flag: (
      <svg className="w-full h-full object-cover" viewBox="0 0 640 480">
        <path d="M0 0h640v480H0z" fill="#c60b1e" />
        <path d="M0 120h640v240H0z" fill="#ffc400" />
      </svg>
    ),
  },
  {
    code: 'en',
    label: 'English',
    flag: (
      <svg className="w-full h-full object-cover" viewBox="0 0 640 480">
        <path d="M0 0h640v480H0z" fill="#012169" />
        <path
          d="M75 0l244 181L562 0h78v62L400 241l240 178v61h-80L320 301 81 480H0v-60l239-179L0 64V0h75z"
          fill="#FFF"
        />
        <path
          d="M424 288L640 450v30h-40L384 316v-28zm14-118L640 18v30l-190 142h-12zM0 462l196-146h30L14 480H0v-18zm216-192L0 108V78l228 172v20z"
          fill="#c8102e"
        />
        <path d="M256 0h128v480H256zM0 176h640v128H0z" fill="#FFF" />
        <path d="M280 0h80v480h-80zM0 200h640v80H0z" fill="#c8102e" />
      </svg>
    ),
  },
  {
    code: 'pt',
    label: 'Português',
    flag: (
      <svg className="w-full h-full object-cover" viewBox="0 0 640 480">
        <path d="M0 0h640v480H0z" fill="#009c3b" />
        <path d="M320 40L600 240 320 440 40 240z" fill="#ffdf00" />
        <circle cx="320" cy="240" r="120" fill="#002776" />
      </svg>
    ),
  },
];
import styles from './LoginView.module.css';

export function LoginView() {
  const router = useRouter();
  const {
    step,
    isSubmitting,
    errorMessage,
    setStep,
    iniciarSesionCredenciales,
    validarDesafioMfa,
  } = useAuthFlow();

  const [showPassword, setShowPassword] = useState(false);
  const [idioma, setIdioma] = useState<'es' | 'en' | 'pt'>('es');
  const [showElieTooltip, setShowElieTooltip] = useState(false);

  // Estados de Modales Globales Reutilizables
  const [isSocialModalOpen, setIsSocialModalOpen] = useState(false);
  const [activeSocialNetwork, setActiveSocialNetwork] = useState<RedSocialId>('INSTAGRAM');
  const [isIdiomaModalOpen, setIsIdiomaModalOpen] = useState(false);

  const currentLang = IDIOMAS.find((item) => item.code === idioma) || IDIOMAS[0];

  const handleOpenSocial = (red: RedSocialId) => {
    setActiveSocialNetwork(red);
    setIsSocialModalOpen(true);
  };

  const handleOpenWhatsApp = () => {
    setActiveSocialNetwork('WHATSAPP');
    setIsSocialModalOpen(true);
  };

  const handleVolver = () => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      router.back();
    } else {
      router.push(ROUTES.HOME);
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginCredencialesSchemaType>({
    resolver: zodResolver(loginCredencialesSchema),
    defaultValues: {
      identificador: '',
      password: '',
      rememberMe: true,
    },
  });

  const onSubmit = async (data: LoginCredencialesSchemaType) => {
    await iniciarSesionCredenciales(data.identificador, data.password, data.rememberMe);
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.mainCanvas}>
        {/* ─── Encabezado Exclusivo de Móvil (Único elemento con fondo de color) ─── */}
        <header className={styles.mobileHeader} data-purpose="mobile-header">
          <Link href={ROUTES.HOME} className="flex items-center gap-2" title="AviFácil — Inicio">
            <div className="w-8 h-8 rounded-full bg-[#FFB800] p-1 flex items-center justify-center shadow-md overflow-hidden flex-shrink-0">
              <Image
                src="/Sign-up/avifacil-logo-badge.png"
                alt="AviFácil Iconotipo"
                width={22}
                height={22}
                className="w-full h-full object-contain pointer-events-none"
                priority
              />
            </div>
            <span
              className="text-2xl text-white tracking-wide drop-shadow-sm font-bold leading-none"
              style={{ fontFamily: "'Caveat', cursive" }}
            >
              AviFácil
            </span>
          </Link>

          {/* Acciones Móviles: Botón Registrarme con Diseño + Botón de Idioma */}
          <div className="flex items-center gap-2">
            <Link
              href={ROUTES.AUTH.REGISTER}
              className={styles.mobileRegisterBtn}
              title="Registrar mi granja"
            >
              <UserPlus size={15} className="text-[#c25ee8] flex-shrink-0" />
              <span>Registrarme</span>
            </Link>

            <button
              type="button"
              onClick={() => setIsIdiomaModalOpen(true)}
              className={styles.mobileLangBtn}
              aria-label={`Idioma actual: ${currentLang.label}. Toca para cambiar.`}
              title={currentLang.label}
            >
              <span className={styles.mobileLangFlag}>
                {currentLang.flag}
              </span>
            </button>
          </div>
        </header>

        {/* ─── Columna Izquierda Desktop (45%): Logo, Redes Sociales y Slot de Elie ─── */}
        <div className={styles.leftColumn}>
          {/* Logotipo AviFácil */}
          <div className={styles.logoContainer}>
            <Link
              href={ROUTES.HOME}
              className="flex items-center gap-2.5 group"
              title="AviFácil — Inicio"
            >
              <div
                className="rounded-full bg-[#FFB800] flex items-center justify-center shadow-md transition-transform duration-200 group-hover:scale-105 flex-shrink-0"
                style={{ width: '36px', height: '36px' }}
              >
                <Image
                  src="/Sign-up/avifacil-logo-badge.png"
                  alt="Logo AviFácil"
                  width={22}
                  height={22}
                  priority
                  className="object-contain drop-shadow-xs w-5 h-5 pointer-events-none"
                />
              </div>
              <span
                className="text-white font-bold text-2xl lg:text-[26px] tracking-wide select-none drop-shadow-xs leading-none"
                style={{ fontFamily: "'Caveat', cursive" }}
              >
                AviFácil
              </span>
            </Link>
          </div>

          {/* Iconos de Redes Sociales Verticales Desktop */}
          <div className={styles.socialContainer}>
            <button
              type="button"
              aria-label="Instagram"
              title="Instagram AviFácil"
              className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-[#c25ee8] shadow-xs hover:scale-110 transition-transform duration-200 cursor-pointer border-0"
              onClick={() => handleOpenSocial('INSTAGRAM')}
            >
              <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
              </svg>
            </button>
            <button
              type="button"
              aria-label="Facebook"
              title="Facebook AviFácil"
              className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-[#c25ee8] shadow-xs hover:scale-110 transition-transform duration-200 cursor-pointer border-0"
              onClick={() => handleOpenSocial('FACEBOOK')}
            >
              <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
            </button>
            <button
              type="button"
              aria-label="YouTube"
              title="YouTube AviFácil"
              className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-[#c25ee8] shadow-xs hover:scale-110 transition-transform duration-200 cursor-pointer border-0"
              onClick={() => handleOpenSocial('YOUTUBE')}
            >
              <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
              </svg>
            </button>
            <button
              type="button"
              aria-label="Google"
              title="Google AviFácil"
              className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-[#c25ee8] shadow-xs hover:scale-110 transition-transform duration-200 cursor-pointer border-0"
              onClick={() => handleOpenSocial('GOOGLE')}
            >
              <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                <path d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.344-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z" />
              </svg>
            </button>
            <button
              type="button"
              aria-label="TikTok"
              title="TikTok AviFácil"
              className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-[#c25ee8] shadow-xs hover:scale-110 transition-transform duration-200 cursor-pointer border-0"
              onClick={() => handleOpenSocial('TIKTOK')}
            >
              <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.24 1.07-.14 1.61.24 1.64 1.82 2.89 3.5 2.77 1.45-.04 2.76-.98 3.19-2.36.14-.48.2-1 .2-1.51.01-5.77.01-11.53.01-17.3z" />
              </svg>
            </button>
          </div>
        </div>

        {/* ─── Tarjeta Blanca (Desktop 65% | Bottom Sheet Móvil cubriendo todo) ─── */}
        <div className={styles.rightCard}>
          {/* Botón Volver en la esquina superior izquierda de la columna derecha */}
          <div className={styles.desktopTopLeftActions}>
            <button
              type="button"
              onClick={handleVolver}
              className={styles.topActionBtn}
              aria-label="Volver"
            >
              <ArrowLeft size={16} className="text-[#374151]" />
              <span>Volver</span>
            </button>
          </div>

          {/* Acciones superiores (Desktop) */}
          <div className={styles.desktopTopActions}>
            <Link href={ROUTES.AUTH.REGISTER} className={styles.topActionBtn}>
              <UserPlus size={15} className="text-[#c25ee8]" />
              <span>Registrarme</span>
            </Link>
            <LanguageSelectorPill
              value={idioma}
              onChange={(l) => setIdioma(l)}
            />
          </div>

          <div className={styles.formWrapper}>
            <AnimatePresence mode="wait">
              {step === 'MFA_CHALLENGE' ? (
                <motion.div
                  key="mfa"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  className="w-full"
                >
                  <MfaChallengeStep
                    isSubmitting={isSubmitting}
                    errorMessage={errorMessage}
                    onVerify={validarDesafioMfa}
                    onBack={() => setStep('CREDENTIALS')}
                  />
                </motion.div>
              ) : (
                <motion.div
                  key="form"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  className="w-full"
                >
                  <h1 className={styles.title}>
                    ¡Hola de{' '}
                    <span
                      className={styles.highlightMarker}
                      style={{
                        fontFamily: "'Caveat', cursive",
                        fontSize: '1.25em',
                        fontWeight: 700,
                        lineHeight: 1,
                        display: 'inline-block',
                      }}
                    >
                      nuevo
                      <svg
                        className={styles.highlightSvg}
                        viewBox="0 0 100 12"
                        preserveAspectRatio="none"
                      >
                        <path
                          d="M0,7 Q25,12 50,7 T100,7"
                          fill="none"
                          stroke="#c25ee8"
                          strokeWidth="3.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    </span>{' '}
                    !
                  </h1>
                  <p className={styles.subtitle}>
                    Ingresa tus credenciales para acceder a tu granja.
                  </p>

                  <form onSubmit={handleSubmit(onSubmit)} className={styles.formContainer}>
                    {/* Banner de Error */}
                    {errorMessage && (
                      <div className={styles.errorBanner} role="alert">
                        <AlertCircle size={17} className="flex-shrink-0" />
                        <span>{errorMessage}</span>
                      </div>
                    )}

                    {/* Campo: Correo o Celular */}
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1.5">
                        Correo Electrónico o Celular <span className="text-[#c25ee8]">*</span>
                      </label>
                      <div className={styles.inputWrapper}>
                        <Mail size={17} className={styles.inputIcon} />
                        <input
                          type="text"
                          autoComplete="username"
                          placeholder="ej. carlos@granja.com o +57 300 123 4567"
                          className={`${styles.inputField} ${
                            errors.identificador ? styles.inputError : ''
                          }`}
                          {...register('identificador')}
                        />
                      </div>
                      {errors.identificador && (
                        <p className="text-xs text-red-500 font-semibold mt-1">
                          {errors.identificador.message}
                        </p>
                      )}
                    </div>

                    {/* Campo: Contraseña */}
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="block text-xs font-bold text-gray-700">
                          Contraseña <span className="text-[#c25ee8]">*</span>
                        </label>
                      </div>
                      <div className={styles.inputWrapper}>
                        <Lock size={17} className={styles.inputIcon} />
                        <input
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="current-password"
                          placeholder="••••••••••••"
                          className={`${styles.inputField} ${
                            errors.password ? styles.inputError : ''
                          }`}
                          {...register('password')}
                        />
                        <button
                          type="button"
                          tabIndex={-1}
                          onClick={() => setShowPassword((prev) => !prev)}
                          className={styles.eyeToggleBtn}
                          aria-label={showPassword ? 'Ocultar contraseña' : 'Ver contraseña'}
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      {errors.password && (
                        <p className="text-xs text-red-500 font-semibold mt-1">
                          {errors.password.message}
                        </p>
                      )}
                    </div>

                    {/* Fila: Recordarme */}
                    <div className="flex items-center justify-between pt-1">
                      <label className={styles.checkboxContainer}>
                        <input
                          type="checkbox"
                          className={styles.checkboxInput}
                          {...register('rememberMe')}
                        />
                        <span>Mantener sesión activa en este equipo</span>
                      </label>
                    </div>

                    {/* Botón CTA */}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={styles.submitBtn}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          <span>Iniciando sesión...</span>
                        </>
                      ) : (
                        <>
                          <span>Iniciar Sesión</span>
                          <ArrowRight size={17} />
                        </>
                      )}
                    </button>
                  </form>

                  {/* Footer del Formulario */}
                  <div className={styles.formFooter}>
                    <p>
                      ¿No tienes una cuenta aún?{' '}
                      <Link href={ROUTES.AUTH.REGISTER} className={styles.registerLink}>
                        Registra tu granja aquí →
                      </Link>
                    </p>
                  </div>

                  {/* Barra Horizontal de Redes Sociales (Exclusiva de Móvil) */}
                  <div className={styles.mobileSocialBar}>
                    <button
                      type="button"
                      aria-label="Instagram"
                      className={styles.mobileSocialBtn}
                      onClick={() => handleOpenSocial('INSTAGRAM')}
                    >
                      <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      aria-label="Facebook"
                      className={styles.mobileSocialBtn}
                      onClick={() => handleOpenSocial('FACEBOOK')}
                    >
                      <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      aria-label="YouTube"
                      className={styles.mobileSocialBtn}
                      onClick={() => handleOpenSocial('YOUTUBE')}
                    >
                      <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      aria-label="Google"
                      className={styles.mobileSocialBtn}
                      onClick={() => handleOpenSocial('GOOGLE')}
                    >
                      <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                        <path d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.344-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      aria-label="TikTok"
                      className={styles.mobileSocialBtn}
                      onClick={() => handleOpenSocial('TIKTOK')}
                    >
                      <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.24 1.07-.14 1.61.24 1.64 1.82 2.89 3.5 2.77 1.45-.04 2.76-.98 3.19-2.36.14-.48.2-1 .2-1.51.01-5.77.01-11.53.01-17.3z" />
                      </svg>
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Espaciador móvil para permitir scroll cómodo por encima de los botones flotantes */}
            <div className={styles.mobileScrollSpacer} aria-hidden="true" />
          </div>
        </div>

        {/* Recorte de Elie Saludando Desktop con Framer Motion */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className={styles.elieContainer}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/Sign-up/elie-saludando.webp"
            alt="Elie saludando en AviFácil"
            className={styles.elieImg}
          />
        </motion.div>

        {/* ─── Botones Flotantes Desktop (WhatsApp y Avatar Elie) ─── */}
        <div className={styles.floatingElieAvatarDesktop}>
          {showElieTooltip && (
            <div className="absolute bottom-16 right-0 w-64 bg-white rounded-2xl p-3 shadow-xl border border-purple-100 text-xs text-gray-700 mb-2 animate-in fade-in slide-in-from-bottom-2 duration-200 z-40 text-left">
              <div className="flex items-start justify-between gap-1 mb-1">
                <span className="font-bold text-[#c25ee8]">¡Hola! Soy Elie 🐥</span>
                <button
                  type="button"
                  onClick={() => setShowElieTooltip(false)}
                  className="text-gray-400 hover:text-gray-600 text-xs cursor-pointer"
                >
                  ✕
                </button>
              </div>
              <p className="text-[11px] text-gray-600 leading-relaxed">
                ¿Problemas para acceder a tu granja? Si olvidaste tu contraseña o necesitas soporte, ¡estoy para ayudarte!
              </p>
            </div>
          )}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowElieTooltip((prev) => !prev)}
              aria-label="Abrir ayuda de Elie Asistente"
              className="w-14 h-14 rounded-full bg-[#c25ee8] border-2 border-white shadow-xl overflow-hidden flex items-center justify-center cursor-pointer transition-transform duration-200 hover:scale-105 focus:outline-none"
            >
              <Image
                src="/Sign-up/elie-avatar.webp"
                alt="Avatar de Elie Asistente"
                width={56}
                height={56}
                className="w-full h-full object-cover"
                style={{ objectPosition: 'center top' }}
              />
            </button>
            <span className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 border-2 border-white rounded-full shadow-xs pointer-events-none z-10" />
          </div>
        </div>

        <div className={styles.floatingWhatsappDesktop}>
          <span className={`absolute w-14 h-14 rounded-full pointer-events-none bg-[#c25ee8] ${styles.waRadarRing}`} />
          <span className={`absolute w-14 h-14 rounded-full pointer-events-none bg-[#c25ee8] ${styles.waRadarRingDelayed}`} />
          <button
            type="button"
            aria-label="Contactar por WhatsApp"
            className={`relative flex items-center justify-center w-14 h-14 rounded-full text-white transition-all duration-200 hover:scale-105 active:scale-95 cursor-pointer group bg-[#c25ee8] hover:bg-[#b048d6] border-0 ${styles.waGlowBtn}`}
            onClick={handleOpenWhatsApp}
          >
            <svg
              className="w-8 h-8 fill-current drop-shadow-xs transition-transform duration-200 group-hover:scale-105"
              viewBox="0 0 24 24"
            >
              <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.582 2.128 2.182-.573c.978.58 1.911.928 3.145.929 3.178 0 5.767-2.587 5.768-5.766.001-3.187-2.575-5.77-5.764-5.771zm3.392 8.244c-.144.405-.837.774-1.17.824-.312.045-.694.06-2.006-.484-1.579-.655-2.613-2.257-2.695-2.366-.08-.109-.646-.86-.646-1.639 0-.779.408-1.163.553-1.32.145-.157.319-.196.425-.196.106 0 .213.001.305.006.1.005.234-.038.366.279.136.327.464 1.132.505 1.215.041.083.068.18.014.288-.053.109-.08.177-.16.27-.08.093-.169.208-.241.28-.08.08-.163.167-.07.327.093.16.413.682.887 1.103.61.543 1.124.71 1.284.79.16.08.254.068.349-.041.095-.109.407-.474.516-.638.109-.164.218-.136.368-.082.15.054.954.45 1.118.532.164.082.273.123.313.191.041.069.041.402-.103.807zM12 2C6.477 2 2 6.477 2 12c0 1.891.527 3.66 1.443 5.176L2 22l4.981-1.306A9.957 9.957 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18.167c-1.697 0-3.272-.51-4.585-1.385l-.329-.22-2.96.776.79-2.885-.24-.383A8.134 8.134 0 013.833 12c0-4.503 3.664-8.167 8.167-8.167 4.503 0 8.167 3.664 8.167 8.167 0 4.503-3.664 8.167-8.167 8.167z" />
            </svg>
          </button>
        </div>
      </div>

      {/* ─── Widget de Soporte Flotante Móvil (WhatsApp y Elie) ─── */}
      <aside className={styles.floatingSupportMobile} data-purpose="floating-support">
        {/* Avatar Elie con tooltip móvil */}
        <div className="relative cursor-pointer group" title="Habla con Elie Asistente">
          {showElieTooltip && (
            <div className="absolute bottom-12 right-0 w-60 bg-white rounded-2xl p-3 shadow-xl border border-purple-100 text-xs text-gray-700 mb-2 animate-in fade-in slide-in-from-bottom-2 duration-200 z-40 text-left">
              <div className="flex items-start justify-between gap-1 mb-1">
                <span className="font-bold text-[#c25ee8]">¡Hola! Soy Elie 🐥</span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowElieTooltip(false);
                  }}
                  className="text-gray-400 hover:text-gray-600 text-xs cursor-pointer"
                >
                  ✕
                </button>
              </div>
              <p className="text-[11px] text-gray-600 leading-relaxed">
                ¿Problemas para acceder a tu granja? Si necesitas soporte, ¡contáctanos por WhatsApp!
              </p>
            </div>
          )}
          <button
            type="button"
            onClick={() => setShowElieTooltip((prev) => !prev)}
            className="w-10 h-10 rounded-full border-2 border-white shadow-md overflow-hidden transition active:scale-95 flex items-center justify-center cursor-pointer p-0 bg-[#c25ee8]"
            aria-label="Ayuda de Elie"
          >
            <Image
              src="/Sign-up/elie-avatar.webp"
              alt="Elie"
              width={40}
              height={40}
              className="w-full h-full object-cover"
              style={{ objectPosition: 'center top' }}
            />
          </button>
          <span className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 border-2 border-white rounded-full pointer-events-none" />
        </div>

        {/* Botón WhatsApp Móvil */}
        <button
          type="button"
          aria-label="Contactar por WhatsApp"
          className="w-11 h-11 rounded-full text-white flex items-center justify-center shadow-lg transition-transform cursor-pointer active:scale-90 bg-[#c25ee8] hover:bg-[#b145dc] border-0"
          onClick={handleOpenWhatsApp}
        >
          <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
            <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.582 2.128 2.182-.573c.978.58 1.911.928 3.145.929 3.178 0 5.767-2.587 5.768-5.766.001-3.187-2.575-5.77-5.764-5.771zm3.392 8.244c-.144.405-.837.774-1.17.824-.312.045-.694.06-2.006-.484-1.579-.655-2.613-2.257-2.695-2.366-.08-.109-.646-.86-.646-1.639 0-.779.408-1.163.553-1.32.145-.157.319-.196.425-.196.106 0 .213.001.305.006.1.005.234-.038.366.279.136.327.464 1.132.505 1.215.041.083.068.18.014.288-.053.109-.08.177-.16.27-.08.093-.169.208-.241.28-.08.08-.163.167-.07.327.093.16.413.682.887 1.103.61.543 1.124.71 1.284.79.16.08.254.068.349-.041.095-.109.407-.474.516-.638.109-.164.218-.136.368-.082.15.054.954.45 1.118.532.164.082.273.123.313.191.041.069.041.402-.103.807zM12 2C6.477 2 2 6.477 2 12c0 1.891.527 3.66 1.443 5.176L2 22l4.981-1.306A9.957 9.957 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18.167c-1.697 0-3.272-.51-4.585-1.385l-.329-.22-2.96.776.79-2.885-.24-.383A8.134 8.134 0 013.833 12c0-4.503 3.664-8.167 8.167-8.167 4.503 0 8.167 3.664 8.167 8.167 0 4.503-3.664 8.167-8.167 8.167z" />
          </svg>
        </button>
      </aside>

      {/* ─── Modal Individual de Red Social o Canal Oficial (Diseño Propio) ─── */}
      <SocialIndividualModal
        isOpen={isSocialModalOpen}
        onClose={() => setIsSocialModalOpen(false)}
        network={activeSocialNetwork}
      />

      {/* ─── Modal Global Selector de Idioma (Móvil y Escritorio) ─── */}
      <IdiomaModalSelector
        isOpen={isIdiomaModalOpen}
        currentCode={idioma}
        idiomas={IDIOMAS}
        onSelect={(item) => {
          setIdioma(item.code);
          setIsIdiomaModalOpen(false);
        }}
        onClose={() => setIsIdiomaModalOpen(false)}
      />
    </div>
  );
}
